/**
 * Wrapper mode selection - Path A (gateway) vs Path B (ingestion).
 *
 * Decides, before each `langwatch <tool>` invocation, which routing
 * shape to apply:
 *
 *   - Path A (gateway): VK present + provider configured + user
 *     hasn't opted out -> inject the base-URL swap envs from
 *     envForTool(). Gateway captures I/O server-side; no OTel
 *     emission from the child.
 *   - Path B (ingestion): no VK (Claude Max-style subscription,
 *     user explicitly opted in) -> mint (or reuse a cached) personal
 *     ingest key (sk-lw-*) for this tool, write the [otel] activation
 *     block to ~/.codex/config.toml (codex only), return the OTel
 *     exporter env block for the child.
 *
 * The two modes are mutually exclusive per the no-double-trace
 * rule - gateway capture + OTel emission of the same call would
 * double-count both traces and cost.
 *
 * Persisted preference lives at cfg.tool_mode[tool]; an unset
 * entry resolves at runtime as "gateway if VK present else
 * ingestion" with no prompt. Future iterations can layer a
 * first-run prompt similar to shell-rc.ts on top.
 */

import {
  writeCodexGatewayBlock,
  writeCodexOtelBlock,
} from "@/cli/utils/codex-config-toml";
import { setOpencodeOpenTelemetryFlag } from "@/cli/utils/opencode-config-flag";

import { lwTag } from "./brand";
import type { GovernanceConfig } from "./config";
import { saveConfig } from "./config";
import {
  extractLookupIdFromToken,
  GovernanceCliError,
  listIngestionKeys,
  mintIngestionKey,
} from "./cli-api";
import { warnIfGeminiOAuthSelected } from "./gemini-settings-preflight";
import { resolvePlatformToolPolicy } from "./platform-tool-policy";

export type WrapperMode = "gateway" | "ingestion";

export interface WrapperModeResult {
  mode: WrapperMode;
  /** Env additions to merge into the child process.env. */
  vars: Record<string, string>;
  /**
   * Path of the codex config.toml that was created / updated. Set
   * for both codex Path A (writes [model_providers.langwatch]) and
   * Path B (writes [otel]).
   */
  codexConfigPath?: string;
  /**
   * Path of the sibling profile file
   * (~/.codex/langwatch-gateway.config.toml). Set only on codex
   * Path A. codex 0.134+ requires the profile body in a separate
   * file when --profile is passed.
   */
  codexProfilePath?: string;
  /**
   * Extra args to prepend to the child invocation. Used for codex
   * Path A: `--profile langwatch-gateway` forces the new provider
   * entry without touching the user's default model_provider.
   */
  extraArgs?: string[];
  /**
   * Env-var names to STRIP from the inherited parent environment
   * before merging the wrapper's vars in. Propagated from the
   * per-tool ToolEnv.clears so the resolver can pass legacy-twin
   * scrubs through to the spawn step (e.g. claude clears
   * ANTHROPIC_API_KEY so claude-code 2.x doesn't warn "Both
   * ANTHROPIC_AUTH_TOKEN and ANTHROPIC_API_KEY set, auth may not
   * work as expected").
   */
  clears?: string[];
  /** True when the wrapper minted a fresh ingest key (vs reused a cached one). */
  newKeyMinted?: boolean;
  /**
   * Path B (ingestion) only: the OTLP base endpoint (`.../api/otel`) and the
   * ingest key. The wrapper uses these AFTER the child exits to POST codex's
   * recovered turn input/output (from the rollout transcript) onto codex's own
   * trace_ids, since codex never puts content on the wire itself.
   */
  endpoint?: string;
  ingestionToken?: string;
  /**
   * Optional one-line notice for the wrapper to print to stderr, set when
   * the platform policy changed the resolved path (e.g. the org admin turned
   * direct OTLP off for this tool, so the wrapper routed through the gateway
   * instead). The member sees why the path differs from the default.
   */
  notice?: string;
}

const SOURCE_TYPE_BY_TOOL: Record<string, string> = {
  claude: "claude_code",
  codex: "codex",
  gemini: "gemini",
  opencode: "opencode",
};

/**
 * Resolve mode for a single tool invocation. Returns the env block
 * the wrapper should hand to the child process. May persist a
 * refreshed ingestion token cache to ~/.langwatch/config.json as a
 * side effect.
 *
 * Does NOT prompt the user. The path-selection UX (interactive select
 * when both paths are allowed) lives upstream in `resolveWrapperPath`,
 * which passes its decision in via `forcedMode`. When `forcedMode` is
 * omitted the resolver falls back to the legacy state-only derivation
 * (persisted tool_mode, else VK-present-implies-gateway).
 *
 * The platform policy still GATES the resolved mode here (downgrade /
 * throw) regardless of how the mode was chosen, so a forced mode the
 * org admin disabled is handled the same as before.
 */
export async function resolveWrapperMode(
  cfg: GovernanceConfig,
  tool: string,
  gatewayVars: Record<string, string>,
  gatewayClears: string[] = [],
  forcedMode?: WrapperMode,
): Promise<WrapperModeResult> {
  const persistedMode = cfg.tool_mode?.[tool];
  const hasVk = !!cfg.default_personal_vk?.secret;
  // Prefer the per-(org, tool) policy the CLI cached at login
  // (cfg.tool_policies, from the control plane's PlatformToolPolicyService).
  // An offline / legacy CLI with no cached map falls back to the hardcoded
  // defaults inside the resolver.
  const policy = resolvePlatformToolPolicy(tool, cfg.tool_policies);

  if (!policy.allowVk && !policy.allowOtelDirect) {
    throw new GovernanceCliError(
      403,
      "tool_disabled",
      `Tool '${tool}' is disabled in the platform policy (both gateway and direct OTLP paths off). Ask your org admin to enable allow_vk or allow_otel_direct.`,
    );
  }

  let notice: string | undefined;

  // EFFECTIVE mode rules:
  //   forcedMode set        -> use it (the path-selection UX upstream
  //                            already applied flag / pref / prompt /
  //                            single-allowed-path; we just honor it).
  //   persisted="gateway"   -> gateway (even if VK absent; preflight surfaces the gap)
  //   persisted="ingestion" -> ingestion
  //   persisted="ask" / unset:
  //     hasVk -> gateway (no surprise: VK users keep current behavior)
  //     no VK -> ingestion (auto-install Path B; closes the "$5 VPS" scenario)
  //
  // Platform policy then GATES the resolved mode (the both-disabled case
  // already threw above, so exactly one path is available when a swap is
  // needed):
  //   - mode=gateway + !allowVk          -> downgrade to ingestion
  //   - mode=ingestion + !allowOtelDirect -> route through the gateway
  //     (never minting an ingestion key the admin disabled)
  let mode: WrapperMode =
    forcedMode ??
    (persistedMode === "gateway"
      ? "gateway"
      : persistedMode === "ingestion"
        ? "ingestion"
        : hasVk
          ? "gateway"
          : "ingestion");

  // Symmetric fall-back: when the resolved mode is disabled but the
  // OTHER mode is allowed, swap into it rather than throwing. Lets
  // cursor (allowVk=true, allowOtelDirect=false) keep working via
  // gateway when no VK is yet configured (preflight surfaces the
  // missing VK separately, same as before this gate existed).
  //
  // The direct-OTLP gate sits ABOVE the ingestion-key mint below: when
  // the admin disabled direct OTLP for this tool, the wrapper never
  // reaches mintIngestionKey; it routes through the gateway (allowVk is
  // guaranteed true here, since the both-disabled case threw above).
  if (mode === "gateway" && !policy.allowVk) {
    mode = "ingestion";
    notice = `${lwTag()} gateway path is disabled for ${tool} by your org admin; using direct OTLP ingestion instead.`;
  }
  if (mode === "ingestion" && !policy.allowOtelDirect) {
    mode = "gateway";
    notice = `${lwTag()} direct OTLP ingestion is disabled for ${tool} by your org admin; routing through the gateway instead.`;
  }

  if (mode === "gateway") {
    if (tool === "gemini") {
      warnIfGeminiOAuthSelected();
    }
    // Codex 0.130+ defers to ChatGPT OAuth by default and ignores
    // OPENAI_API_KEY unless the active model_provider is an
    // explicit env-keyed entry. Write a langwatch provider +
    // profile to ~/.codex/config.toml and force codex into it via
    // `--profile`. Other tools (claude/gemini/cursor/opencode)
    // honour their base-URL+API-key env directly, no toml needed.
    if (tool === "codex") {
      const gw = writeCodexGatewayBlock({
        gatewayUrl: cfg.gateway_url,
        envKey: "OPENAI_API_KEY",
      });
      return {
        mode,
        vars: gatewayVars,
        clears: gatewayClears,
        codexConfigPath: gw.path,
        codexProfilePath: gw.profilePath,
        extraArgs: ["--profile", gw.profile],
        notice,
      };
    }
    return { mode, vars: gatewayVars, clears: gatewayClears, notice };
  }

  // INGESTION mode: ensure key + (for codex) toml.
  const sourceType = SOURCE_TYPE_BY_TOOL[tool];
  if (!sourceType) {
    // No ingestion template defined for this tool (cursor is the
    // current example - GUI app, no useful OTel). Fall through to
    // gateway shape; the existing preflight will tell the user
    // what's missing.
    return { mode: "gateway", vars: gatewayVars, clears: gatewayClears, notice };
  }

  // Defense-in-depth: the direct-OTLP gate above already routes to the
  // gateway when allowOtelDirect is off, so this mint is unreachable in
  // that case. Guard it explicitly so a future refactor of the gate can
  // never silently mint an ingestion key the admin disabled.
  if (!policy.allowOtelDirect) {
    throw new GovernanceCliError(
      403,
      "otel_direct_disabled",
      `Direct OTLP ingestion is disabled for '${tool}' by your org admin. Ask them to enable allow_otel_direct, or run with the gateway path.`,
    );
  }

  // Reuse a cached personal ingest key (sk-lw-*) for this source when
  // present; otherwise mint a fresh one. The mint route returns the
  // plaintext key once, so we persist it to the per-tool cache below
  // and read it back on subsequent invocations rather than re-minting.
  //
  // Stale-cache check (#4755): before reusing a cached key, confirm it is
  // still live on the platform. Token format: `ik-lw-{16-char lookupId}_{secret}`.
  // If the server resolves and the lookupId is absent → the key was revoked
  // (hard-cut rotation also invalidates the cache) → mint fresh and overwrite.
  // If the request rejects (offline / older server without this endpoint) →
  // offline-first fallback: reuse the cache so air-gapped / degraded environments
  // still work.
  const cached = cfg.default_personal_ingest_keys?.[sourceType];
  let token: string;
  let prefix: string | undefined;
  let endpoint: string;
  let minted: boolean;
  if (cached?.secret) {
    const cachedLookupId = extractLookupIdFromToken(cached.secret);
    let cacheIsLive = true; // assume live; falsified when server confirms otherwise
    try {
      const liveKeys = await listIngestionKeys(cfg);
      // Server resolved — verify the cached lookupId is still present for this sourceType
      const liveEntry = liveKeys.find(
        (k) => k.sourceType === sourceType && k.lookupId === cachedLookupId,
      );
      if (!liveEntry) {
        // Key was revoked or rotated on the platform — treat as no cache
        cacheIsLive = false;
      }
    } catch {
      // Network error / older server without the endpoint: reuse cache as-is
      // (offline-first fallback — hard-cut rotation is a re-mint-kills-old
      // invariant, so a genuinely revoked key will self-correct next time the
      // device is online).
    }
    if (cacheIsLive) {
      token = cached.secret;
      prefix = cached.prefix;
      endpoint = `${cfg.control_plane_url.replace(/\/+$/, "")}/api/otel`;
      minted = false;
    } else {
      const r = await mintIngestionKey(cfg, sourceType);
      token = r.token;
      prefix = r.prefix;
      endpoint = r.endpoint;
      minted = true;
    }
  } else {
    const r = await mintIngestionKey(cfg, sourceType);
    token = r.token;
    prefix = r.prefix;
    endpoint = r.endpoint;
    minted = true;
  }

  const vars = buildOtelEnvBlock(tool, endpoint, token);

  let codexConfigPath: string | undefined;
  if (tool === "codex") {
    // codex's OTLP/HTTP exporter sends every signal to the configured
    // endpoint verbatim - it does NOT append `/v1/traces` the way the
    // OTel SDKs in Node/Python/Go do. Spell the trace-signal suffix
    // out here so the POST lands on the real handler. codex only
    // emits traces today (no logs/metrics), so one suffix suffices.
    const result = writeCodexOtelBlock({
      endpoint: `${endpoint}/v1/traces`,
      ingestionToken: token,
      environment: cfg.organization?.slug ?? "langwatch",
    });
    codexConfigPath = result.path;
  }

  if (tool === "opencode") {
    // opencode constructs its OTLP exporter but only EMITS spans when
    // `experimental.openTelemetry` is true in ~/.config/opencode/opencode.jsonc.
    // Without this the OTEL_EXPORTER_OTLP_* env vars we set below are
    // accepted-and-ignored - Path B silently produces nothing. Idempotent
    // merge: if the user already turned it on, no write; if they
    // explicitly set false, we don't overwrite their intent.
    setOpencodeOpenTelemetryFlag();
  }

  // Persist mode + (when freshly minted) the ingest key so the next
  // invocation skips re-deriving the mode and reuses the cached key
  // instead of minting again.
  const next: GovernanceConfig = {
    ...cfg,
    tool_mode: { ...(cfg.tool_mode ?? {}), [tool]: "ingestion" },
  };
  if (minted) {
    next.default_personal_ingest_keys = {
      ...(cfg.default_personal_ingest_keys ?? {}),
      [sourceType]: { secret: token, prefix },
    };
  }
  try {
    saveConfig(next);
  } catch {
    // Best-effort cache - failure to persist doesn't block this run.
  }

  return {
    mode,
    vars,
    codexConfigPath,
    newKeyMinted: minted,
    notice,
    endpoint,
    ingestionToken: token,
  };
}

function buildOtelEnvBlock(
  tool: string,
  endpoint: string,
  token: string,
): Record<string, string> {
  const base = {
    OTEL_EXPORTER_OTLP_ENDPOINT: endpoint,
    OTEL_EXPORTER_OTLP_HEADERS: `Authorization=Bearer ${token}`,
  };

  switch (tool) {
    case "claude":
      // Three further OTel unlock knobs found in the claude-code 2.x
      // bundled binary string sweep (alongside OTEL_LOG_USER_PROMPTS
      // which we already set), all four officially documented on
      // code.claude.com/docs/en/monitoring-usage:
      //   OTEL_LOG_TOOL_DETAILS  - lifts tool_input / tool_parameters
      //     attrs (Bash command text, Edit diffs, Read file paths,
      //     etc) onto tool_decision + tool_result events. The
      //     receiver-side previously had only `tool_input_size_bytes`
      //     and `tool_result_size_bytes` - proven across the
      //     andre-claude-tool-calls + sergey-third-eye dump set.
      //   OTEL_LOG_TOOL_CONTENT  - TRACES-ONLY + requires beta
      //     tracing. claude 2.x is LOGS-ONLY today so this is a
      //     no-op for us. Set anyway as forward-compat for when
      //     claude turns on traces. Tool I/O on the logs path
      //     actually comes from TOOL_DETAILS + RAW_API_BODIES.
      //   OTEL_LOG_RAW_API_BODIES - emits two NEW event types
      //     `api_request_body` + `api_response_body` carrying the
      //     FULL JSON of every message (system prompts + user content
      //     + assistant text + tool_use blocks). THIS IS THE ONLY
      //     surface that carries the assistant response text - every
      //     other event (api_request, user_prompt, tool_*) is
      //     metadata only. Andre's live-dogfood (proxy intercept on
      //     :4318) found "UNLOCK-KNOBS-TEST-PROOF-7777" in
      //     api_response_body.content[].text with this flag set. Also
      //     the heaviest payload class (system prompts can be 100KB+,
      //     message history grows turn-over-turn) - same fat-payload
      //     class as the CH merge memory-ceiling incident
      //     [[project_skai_ch_merge_memory_ceiling_outage]].
      //
      // Default policy: ALL FOUR knobs ON. rchaves "fix everything,
      // collect all humanly possible". Payload risk is bounded:
      // claude 2.x caps api_request_body + api_response_body at 60KB
      // INLINE per event (inline is the default; the optional
      // file:<dir> mode that writes untruncated bodies to disk is NOT
      // enabled). Alexis ships a complementary receiver-side guard
      // in the same PR as defense-in-depth on fold accumulation +
      // a Body cap in case future claude versions remove the 60KB
      // inline limit. PII / logging-opt-out controls already live on
      // the platform settings page. Note: extended-thinking content
      // is ALWAYS redacted by claude from raw bodies - we cannot
      // capture it regardless of flag state.
      return {
        CLAUDE_CODE_ENABLE_TELEMETRY: "1",
        OTEL_TRACES_EXPORTER: "otlp",
        OTEL_LOGS_EXPORTER: "otlp",
        OTEL_METRICS_EXPORTER: "otlp",
        OTEL_EXPORTER_OTLP_PROTOCOL: "http/json",
        OTEL_LOG_USER_PROMPTS: "1",
        OTEL_LOG_TOOL_DETAILS: "1",
        OTEL_LOG_TOOL_CONTENT: "1",
        OTEL_LOG_RAW_API_BODIES: "1",
        ...base,
        OTEL_RESOURCE_ATTRIBUTES: "service.name=claude-code",
      };
    case "codex":
      return {
        OTEL_TRACES_EXPORTER: "otlp",
        OTEL_EXPORTER_OTLP_PROTOCOL: "http/json",
        ...base,
        OTEL_RESOURCE_ATTRIBUTES: "service.name=codex",
      };
    case "gemini":
      // gemini-cli 0.46 telemetry resolver (packages/core/dist/src/telemetry/config.js):
      //   target ∈ {local, gcp} - NOT otlp. The JSON-schema doc string
      //   mentions otlp as an "example", but the runtime validator
      //   (parseTelemetryTargetValue) only accepts local|gcp; passing
      //   otlp throws FatalConfigError at startup.
      //   To forward spans + log records to our OTLP endpoint we use
      //   `local` (in-process exporters) + `useCollector=true` which
      //   routes through @opentelemetry/exporter-trace-otlp-http +
      //   exporter-logs-otlp-http to GEMINI_TELEMETRY_OTLP_ENDPOINT
      //   (falls back to OTEL_EXPORTER_OTLP_ENDPOINT).
      //   `traces=true` enables the detail-attribute span path so the
      //   user prompt + tool calls land as span attributes (not just
      //   token counts).
      //   `logPrompts=true` is what makes gemini-cli embed the actual
      //   user prompt text in the user_prompt event - without it the
      //   receiver-side fold has no input text to lift onto
      //   langwatch.input.value, same class as claude-code.
      return {
        GEMINI_TELEMETRY_ENABLED: "true",
        GEMINI_TELEMETRY_TARGET: "local",
        GEMINI_TELEMETRY_USE_COLLECTOR: "true",
        GEMINI_TELEMETRY_TRACES_ENABLED: "true",
        GEMINI_TELEMETRY_OTLP_PROTOCOL: "http",
        GEMINI_TELEMETRY_OTLP_ENDPOINT: endpoint,
        GEMINI_TELEMETRY_LOG_PROMPTS: "true",
        OTEL_TRACES_EXPORTER: "otlp",
        OTEL_EXPORTER_OTLP_PROTOCOL: "http/json",
        ...base,
        OTEL_RESOURCE_ATTRIBUTES: "service.name=gemini-cli",
      };
    case "opencode":
      return {
        OTEL_TRACES_EXPORTER: "otlp",
        OTEL_LOGS_EXPORTER: "otlp",
        OTEL_METRICS_EXPORTER: "otlp",
        OTEL_EXPORTER_OTLP_PROTOCOL: "http/json",
        ...base,
        OTEL_RESOURCE_ATTRIBUTES: "service.name=opencode",
      };
    default:
      return base;
  }
}
